import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardShell from "@/components/DashboardShell";
import { EMPLOYER_NAV } from "./nav";
import api from "@/lib/api";
import { toast } from "sonner";
import { Building2, PlusCircle, CheckCircle2, ChevronRight, Users, MapPin, Briefcase } from "lucide-react";

const PIPELINE = ["Applied", "Under Review", "Shortlisted", "Interview", "Selected", "Rejected"];
const PIPELINE_COLORS = {
  Applied: "bg-blue-500", "Under Review": "bg-amber-500", Shortlisted: "bg-purple-500",
  Interview: "bg-indigo-500", Selected: "bg-emerald-500", Rejected: "bg-red-400",
};

export default function EmployerDashboard() {
  const [data, setData] = useState(null);
  const [company, setCompany] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [selJob, setSelJob] = useState(null);
  const [apps, setApps] = useState([]);

  useEffect(() => {
    api.get("/employers/dashboard").then((r) => setData(r.data)).catch(() => {});
    api.get("/employers/me").then((r) => setCompany(r.data)).catch(() => {});
    api.get("/jobs/mine").then((r) => setJobs(r.data)).catch(() => {});
  }, []);

  const openJob = async (job) => {
    setSelJob(job);
    setApps([]);
    const r = await api.get(`/employers/jobs/${job.job_id}/applications`);
    setApps(r.data);
  };

  const changeStatus = async (appId, status) => {
    await api.put(`/employers/applications/${appId}/status`, { status });
    setApps((a) => a.map((x) => x.application_id === appId ? { ...x, status } : x));
    toast.success(`Marked as ${status}`);
  };

  const sc = data?.status_counts || {};

  return (
    <DashboardShell nav={EMPLOYER_NAV}>
      {/* Company header — visually distinct from candidate dashboard */}
      <div className="bg-slate-900 rounded-2xl p-6 sm:p-8 text-white relative overflow-hidden" data-testid="employer-header">
        <div className="absolute inset-0 hero-grid opacity-30" />
        <div className="relative flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl copper-gradient flex items-center justify-center shrink-0">
              <Building2 className="w-7 h-7 text-white" />
            </div>
            <div>
              <span className="text-xs font-mono-tech uppercase tracking-widest text-[#D96B27]">Employer Account</span>
              <h1 className="font-display text-2xl font-bold">{company?.name || "Your Company"}</h1>
              <div className="flex items-center gap-2 mt-1">
                {company?.verified ? (
                  <span className="text-xs bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Verified</span>
                ) : (
                  <span className="text-xs bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded">Verification Pending</span>
                )}
                {company?.industry && <span className="text-xs text-slate-400">{company.industry}</span>}
              </div>
            </div>
          </div>
          <Link to="/employer/post-job" data-testid="post-job-cta" className="flex items-center gap-2 copper-gradient text-white font-semibold px-6 py-3 rounded-lg hover:opacity-95">
            <PlusCircle className="w-5 h-5" />Post a Job
          </Link>
        </div>
      </div>

      {/* Hiring pipeline funnel */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 mt-5" data-testid="hiring-pipeline">
        <h2 className="font-display font-semibold text-slate-900 mb-4">Hiring Pipeline <span className="text-slate-400 text-sm font-normal">({data?.applications ?? 0} total applications)</span></h2>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {PIPELINE.map((stage) => (
            <div key={stage} data-testid={`pipeline-${stage.toLowerCase().replace(/ /g, '-')}`} className="rounded-lg border border-slate-100 p-3 text-center">
              <div className={`w-2 h-2 rounded-full mx-auto mb-1.5 ${PIPELINE_COLORS[stage]}`} />
              <div className="font-display text-xl font-bold text-slate-900">{sc[stage] || sc[stage.toLowerCase()] || 0}</div>
              <div className="text-xs text-slate-500 leading-tight">{stage}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-5 mt-5">
        {/* Jobs table */}
        <div className="lg:col-span-3 bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-display font-semibold text-slate-900">My Job Postings ({jobs.length})</h2>
            <span className="text-xs text-slate-400">{data?.active_jobs ?? 0} active</span>
          </div>
          {jobs.length === 0 ? (
            <div className="text-center py-12">
              <Briefcase className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500 mb-3">You haven't posted any jobs yet.</p>
              <Link to="/employer/post-job" className="text-sm text-[#D96B27] font-medium">Post your first job →</Link>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-400 bg-slate-50 border-b border-slate-100"><th className="py-2.5 px-5 font-medium">Job</th><th className="py-2.5 px-3 font-medium">Applicants</th><th className="py-2.5 px-3 font-medium">Status</th><th className="py-2.5 px-3"></th></tr></thead>
              <tbody>
                {jobs.map((j) => (
                  <tr key={j.job_id} onClick={() => openJob(j)} data-testid={`emp-job-${j.job_id}`} className={`border-b border-slate-50 cursor-pointer hover:bg-slate-50 ${selJob?.job_id === j.job_id ? "bg-[#FFF3EC]" : ""}`}>
                    <td className="py-3 px-5"><div className="font-medium text-slate-800">{j.title}</div><div className="text-xs text-slate-400 flex items-center gap-1"><MapPin className="w-3 h-3" />{j.location} · {j.min_experience}-{j.max_experience} yrs</div></td>
                    <td className="py-3 px-3"><span className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full"><Users className="w-3 h-3" />{j.application_count}</span></td>
                    <td className="py-3 px-3"><span className={`text-xs px-2 py-0.5 rounded ${j.status === "Published" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{j.status}</span></td>
                    <td className="py-3 px-3 text-slate-300"><ChevronRight className="w-4 h-4" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Applicants panel */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-5">
          <h2 className="font-display font-semibold text-slate-900 mb-3">{selJob ? `Applicants — ${selJob.title}` : "Recent Applications"}</h2>
          {selJob && apps.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">No applicants for this job yet.</p>}
          {!selJob && (data?.recent_applications?.length ? null : <p className="text-sm text-slate-400 py-6 text-center">No applications yet. Share your job posting to get applicants.</p>)}
          <div className="space-y-3">
            {(selJob ? apps : (data?.recent_applications || [])).map((a) => (
              <div key={a.application_id} className="border border-slate-100 rounded-lg p-3" data-testid={`emp-app-${a.application_id}`}>
                <div className="flex justify-between items-start gap-2">
                  <div>
                    <div className="font-medium text-sm text-slate-800">{a.candidate?.full_name || "Candidate"}</div>
                    <div className="text-xs text-slate-400">{a.job_title}</div>
                    {a.candidate && <div className="text-xs text-slate-400">{a.candidate.current_designation} · {a.candidate.total_experience} yrs · {a.candidate.cable_experience} yrs cable</div>}
                  </div>
                  <select value={a.status} onChange={(e) => changeStatus(a.application_id, e.target.value)} data-testid={`app-status-${a.application_id}`} className="text-xs border border-slate-200 rounded px-2 py-1">
                    {PIPELINE.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
                {a.candidate && <div className="flex flex-wrap gap-1 mt-2">{(a.candidate.skills || []).slice(0, 5).map((s) => <span key={s} className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{s}</span>)}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </DashboardShell>
  );
}
